import React from 'react';
import { motion } from 'framer-motion';
export interface SentencePart {
  /** The word or phrase */
  text: string;
  /** Grammatical role (e.g., 'onderwerp', 'gezegde', 'lijdend voorwerp') */
  role: string;
  /** Optional abbreviation for compact display */
  abbr?: string;
}
export interface SentenceDiagramProps {
  /** The full sentence */
  sentence: string;
  /** Parsed parts with roles */
  parts: SentencePart[];
  /** Optional label */
  label?: string;
  /** 'stimulus' = compact, 'support' = educational panel */
  mode?: 'stimulus' | 'support';
}
/** Color mapping for Dutch grammatical roles — aligned with Taalverhaal method */
const ROLE_COLORS: Record<string, {
  bg: string;
  border: string;
  text: string;
  underline: string;
}> = {
  // 6 Core Roles
  onderwerp: {
    bg: 'bg-red-100',
    border: 'border-red-300',
    text: 'text-red-800',
    underline: 'border-red-400'
  },
  persoonsvorm: {
    bg: 'bg-blue-100',
    border: 'border-blue-300',
    text: 'text-blue-800',
    underline: 'border-blue-400'
  },
  gezegde: {
    bg: 'bg-sky-100',
    border: 'border-sky-300',
    text: 'text-sky-800',
    underline: 'border-sky-400'
  },
  'werkwoordelijk gezegde': {
    bg: 'bg-sky-100',
    border: 'border-sky-300',
    text: 'text-sky-800',
    underline: 'border-sky-400'
  },
  'naamwoordelijk gezegde': {
    bg: 'bg-sky-100',
    border: 'border-sky-300',
    text: 'text-sky-800',
    underline: 'border-sky-400'
  },
  'lijdend voorwerp': {
    bg: 'bg-green-100',
    border: 'border-green-300',
    text: 'text-green-800',
    underline: 'border-green-400'
  },
  'meewerkend voorwerp': {
    bg: 'bg-amber-100',
    border: 'border-amber-300',
    text: 'text-amber-800',
    underline: 'border-amber-400'
  },
  'bijwoordelijke bepaling': {
    bg: 'bg-purple-100',
    border: 'border-purple-300',
    text: 'text-purple-800',
    underline: 'border-purple-400'
  },
  // Secondary Roles (Mapped to shades of purple/slate)
  'bepaling van tijd': {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    underline: 'border-purple-300'
  },
  'bepaling van plaats': {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    underline: 'border-purple-300'
  },
  'bepaling van wijze': {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    underline: 'border-purple-300'
  },
  voorzetselvoorwerp: {
    bg: 'bg-slate-100',
    border: 'border-slate-300',
    text: 'text-slate-700',
    underline: 'border-slate-400'
  },
  voegwoord: {
    bg: 'bg-slate-100',
    border: 'border-slate-300',
    text: 'text-slate-700',
    underline: 'border-slate-400'
  },
  // Bijzin is handled specially via a wrapper, but we provide a fallback color
  bijzin: {
    bg: 'bg-transparent',
    border: 'border-slate-400 border-dashed',
    text: 'text-slate-600',
    underline: 'border-transparent'
  }
};
const DEFAULT_COLOR = {
  bg: 'bg-gray-100',
  border: 'border-gray-300',
  text: 'text-gray-700',
  underline: 'border-gray-400'
};
/** Standard abbreviations for Dutch grammatical roles */
const ROLE_ABBR: Record<string, string> = {
  onderwerp: 'OW',
  persoonsvorm: 'PV',
  gezegde: 'GZ',
  'werkwoordelijk gezegde': 'WG',
  'naamwoordelijk gezegde': 'NG',
  'lijdend voorwerp': 'LV',
  'meewerkend voorwerp': 'MV',
  'bijwoordelijke bepaling': 'BwB',
  'bepaling van tijd': 'BvT',
  'bepaling van plaats': 'BvP',
  'bepaling van wijze': 'BvW',
  voorzetselvoorwerp: 'VzV',
  bijzin: 'BZ',
  voegwoord: 'VW'
};
function getColor(role: string) {
  const key = role.toLowerCase();
  return ROLE_COLORS[key] ?? DEFAULT_COLOR;
}
function getAbbr(role: string, customAbbr?: string): string {
  if (customAbbr) return customAbbr;
  return ROLE_ABBR[role.toLowerCase()] ?? role.slice(0, 3).toUpperCase();
}
export function SentenceDiagram({
  sentence,
  parts,
  label,
  mode = 'support'
}: SentenceDiagramProps) {
  if (!parts || parts.length === 0) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-12562">
        <p className="text-red-600 text-sm font-medium" data-id="element-12563">
          Geen zinsontleding beschikbaar
        </p>
      </div>;
  }
  // Stimulus mode: compact colored blocks
  if (mode === 'stimulus') {
    return <div className="bg-slate-50 rounded-xl px-5 py-4 border border-slate-200 overflow-x-auto" aria-label={label ?? 'Zinsontleding'} data-id="element-12564">
        {label && <p className="text-sm font-semibold text-slate-800 mb-3 text-center" data-id="element-12565">
            {label}
          </p>}
        <div className="flex items-end gap-1.5 min-w-max" data-id="element-12566">
          {parts.map((part, i) => {
          const isBijzin = part.role.toLowerCase() === 'bijzin';
          const c = getColor(part.role);
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.08,
            duration: 0.25
          }} className={`flex flex-col items-center ${isBijzin ? 'p-1.5 border-2 border-dashed border-slate-400 rounded-lg' : ''}`} data-id="element-12567">
                <span className={`${c.bg} ${c.border} border rounded-md px-2 py-1 text-sm font-medium ${c.text}`} data-id="element-12568">
                  {part.text}
                </span>
                <span className={`text-[10px] font-bold mt-0.5 ${c.text} opacity-70`} data-id="element-12569">
                  {getAbbr(part.role, part.abbr)}
                </span>
              </motion.div>;
        })}
        </div>
      </div>;
  }
  // Support mode: educational panel
  const uniqueRoles = [...new Set(parts.map(p => p.role.toLowerCase()))];
  return <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-6 rounded-2xl border-2 border-slate-200 shadow-sm overflow-x-auto" aria-label={label ?? 'Zinsontleding'} data-id="element-12570">
      {label && <div className="mb-5 text-center" data-id="element-12571">
          <div className="inline-flex items-center gap-2 bg-white/90 rounded-xl px-4 py-2 border border-slate-200 shadow-sm" data-id="element-12572">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" data-id="element-12573" />
            <span className="text-sm font-bold text-slate-900" data-id="element-12574">{label}</span>
          </div>
        </div>}

      {/* Full sentence */}
      <div className="bg-white/80 rounded-xl p-4 border border-slate-100 mb-4" data-id="element-12575">
        <p className="text-sm text-slate-500 font-medium mb-2" data-id="element-12576">Zin:</p>
        <p className="text-base text-slate-900 font-medium italic" data-id="element-12577">
          "{sentence}"
        </p>
      </div>

      {/* Colored diagram */}
      <div className="bg-white/80 rounded-xl p-4 border border-slate-100 mb-4 overflow-x-auto" data-id="element-12578">
        <p className="text-sm text-slate-500 font-medium mb-3" data-id="element-12579">Ontleding:</p>
        <div className="flex items-end gap-2 min-w-max pb-2" data-id="element-12580">
          {parts.map((part, i) => {
          const isBijzin = part.role.toLowerCase() === 'bijzin';
          const c = getColor(part.role);
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.15 + i * 0.1,
            duration: 0.3
          }} className={`flex flex-col items-center ${isBijzin ? 'p-2 border-2 border-dashed border-slate-400 rounded-xl relative' : ''}`} data-id="element-12581">
                {isBijzin && <div className="absolute -top-3 bg-slate-100 px-2 rounded text-[10px] font-bold text-slate-500 border border-slate-300" data-id="element-12582">
                    Bijzin
                  </div>}
                <div className={`${c.bg} ${c.border} border-2 rounded-lg px-3 py-2 font-semibold ${c.text} text-sm border-b-4 ${c.underline}`} data-id="element-12583">
                  {part.text}
                </div>
                <div className={`mt-1 text-xs font-bold ${c.text}`} data-id="element-12584">
                  {getAbbr(part.role, part.abbr)}
                </div>
                <div className={`text-[10px] ${c.text} opacity-60 max-w-[100px] text-center leading-tight`} data-id="element-12585">
                  {part.role}
                </div>
              </motion.div>;
        })}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white/80 rounded-xl p-4 border border-slate-100 shadow-sm" data-id="element-12586">
        <div className="flex items-start gap-3" data-id="element-12587">
          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-12588">
            <span className="text-blue-600 text-sm" data-id="element-12589">🏷️</span>
          </div>
          <div data-id="element-12590">
            <h4 className="text-sm font-bold text-slate-900 mb-2" data-id="element-12591">Legenda</h4>
            <div className="flex flex-wrap gap-2" data-id="element-12592">
              {uniqueRoles.map(role => {
              const c = getColor(role);
              return <div key={role} className="flex items-center gap-1.5" data-id="element-12593">
                    <div className={`w-3 h-3 rounded ${c.bg} ${c.border} border`} data-id="element-12594" />
                    <span className="text-xs text-slate-600" data-id="element-12595">
                      <strong data-id="element-12596">
                        {ROLE_ABBR[role] ?? role.slice(0, 3).toUpperCase()}
                      </strong>{' '}
                      = {role}
                    </span>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </div>;
}